import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import { FloorOverlay } from "generated/graphql";
import {
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import CheckboxList from "./Forms/CheckboxList";
import HeaderBlock from "./HeaderBlock";
import styled from "styled-components";
import ClickLocation from "components/Maps/ClickLocation";
import AddlistItems from "./Forms/AddlistItems";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    height: "100%",
  },
  grid: {},
}));

const Container = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HeightContainer = styled.div`
  height: 100%;
`;

const ChildrenContainer = styled.div`
  width: 100%;
`;

const FormWrapper = styled.form`
  width: 100%;
`;

export interface FormOption {
  value: string | number;
  label: string;
}

export interface ConfigInput {
  helperText?: string;
  required?: boolean;
  type:
    | "text"
    | "select"
    | "image"
    | "radio"
    | "checklist"
    | "custom"
    | "time"
    | "date"
    | "dateTime"
    | "mapPoint"
    | "upload"
    | "custom"
    | "addList";
  options?: FormOption[];
  title: string;
  placeholder?: string;
  selectedOptions?: any[];
  floorOverlays?: Pick<
    FloorOverlay,
    | "BuildingId"
    | "CenterLat"
    | "CenterLng"
    | "D_diff"
    | "FloorNumber"
    | "Id"
    | "ImageUrl"
    | "Name"
    | "Rot"
    | "created_at"
    | "k_diff"
    | "updated_at"
  >[];
  initialPoint?: { lat: number; lng: number; floorNumber: number };
  onNewLocation?: (lat: number, lng: number, floorNumber: number) => void;
  onOptionClick?: (id: string | number) => void;
  onOptionAdd?: (option: FormOption) => void;
  onOptionRemove?: (option: FormOption) => void;
  onOptionEdit?: (option: FormOption) => void;
  id: string;
  value?: string | number;
  customInput?: JSX.Element;
  cancellable?: boolean;
  onCancel?: () => void;
  onChange: (event: any) => void;
  xs:
    | boolean
    | "auto"
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | undefined;
  sm:
    | boolean
    | "auto"
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | undefined;
}

export interface ConfigFormProps {
  children?: JSX.Element[] | JSX.Element;
  inputs: ConfigInput[];
  title?: string;
  description?: string;
}

export default function ConfigForm({
  inputs,
  children,
  title,
  description,
}: ConfigFormProps) {
  const classes = useStyles();
  const isDesktop = useMediaQuery("(min-width:600px)");
  let titleBlock;
  if (title) {
    titleBlock = <HeaderBlock title={title} description={description} />;
  }
  return (
    <Container>
      {titleBlock}
      {/* <FormWrapper className={classes.root} noValidate autoComplete="off"> */}
      <Grid container spacing={3}>
        {inputs.map((input: ConfigInput, index: number) => {
          switch (input.type) {
            case "text":
              return (
                <Grid
                  className={classes.grid}
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <TextField
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                        value={input.value}
                        id={input.id}
                        label={input.title}
                        required={input.required}
                        helperText={input.placeholder}
                      />
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "select":
              return (
                <Grid
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                  className={classes.grid}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <TextField
                        id={input.id}
                        select
                        label="Select"
                        value={input.value}
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                        helperText={input.placeholder}
                      >
                        {input!.options!.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "radio":
              return (
                <Grid
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                  className={classes.grid}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">{input.title}</FormLabel>
                        <RadioGroup
                          aria-label={input.title}
                          name={input.title}
                          value={input.value}
                          onChange={(event) => {
                            input.onChange(event);
                          }}
                        >
                          {input.options?.map((option, index) => {
                            return (
                              <FormControlLabel
                                key={index}
                                value={option.value}
                                control={<Radio />}
                                label={option.label}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "checklist":
              if (
                !!input.onOptionClick &&
                !!input.options &&
                !!input.selectedOptions
              ) {
                return (
                  <Grid
                    key={input.id}
                    item
                    xs={input.xs}
                    sm={input.sm}
                    className={classes.grid}
                  >
                    <Paper className={classes.paper}>
                      <HeightContainer>
                        <Typography color="textPrimary" variant="h5">
                          {input.title}
                        </Typography>
                        <CheckboxList
                          onOptionClick={input.onOptionClick}
                          options={input.options}
                          selectedOptions={input.selectedOptions}
                        />
                      </HeightContainer>
                    </Paper>
                  </Grid>
                );
              } else {
                return (
                  <Grid key={input.id} item xs={input.xs} sm={input.sm}>
                    <Paper className={classes.paper}>Error</Paper>
                  </Grid>
                );
              }
            case "time":
              return (
                <Grid
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                  className={classes.grid}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <Typography color="textPrimary" variant="h5">
                        {input.title}
                      </Typography>
                      <TextField
                        id={input.id}
                        label={input.title}
                        type="time"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          step: 300, // 5 min
                        }}
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                        value={input.value}
                      />
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "date":
              return (
                <Grid
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                  className={classes.grid}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <Typography color="textPrimary" variant="h5">
                        {input.title}
                      </Typography>
                      <TextField
                        id={input.id}
                        label={input.title}
                        type="date"
                        //defaultValue="2017-05-24"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                      />
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "dateTime":
              return (
                <Grid
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                  className={classes.grid}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <Typography color="textPrimary" variant="h5">
                        {input.title}
                      </Typography>
                      <TextField
                        id={input.id}
                        label={input.title}
                        type="datetime-local"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          step: 300, // 5 min
                        }}
                        onChange={(event) => {
                          input.onChange(event);
                        }}
                        value={input.value}
                      />
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "addList":
              if (
                input.options &&
                input.onOptionAdd &&
                input.onOptionEdit &&
                input.onOptionRemove
              ) {
                return (
                  <Grid
                    key={input.id}
                    item
                    xs={input.xs}
                    sm={input.sm}
                    className={classes.grid}
                  >
                    <Paper className={classes.paper}>
                      <HeightContainer>
                        <Typography color="textPrimary" variant="h5">
                          {input.title}
                        </Typography>
                        <AddlistItems
                          options={input.options}
                          onOptionAdd={input.onOptionAdd}
                          onOptionRemove={input.onOptionRemove}
                          onOptionEdit={input.onOptionEdit}
                        />
                      </HeightContainer>
                    </Paper>
                  </Grid>
                );
              }
              break;
            case "custom":
              if (!!input.customInput) {
                return (
                  <Grid
                    key={input.id}
                    item
                    xs={input.xs}
                    sm={input.sm}
                    className={classes.grid}
                  >
                    <Paper className={classes.paper}>
                      <HeightContainer>
                        <Typography color="textPrimary" variant="h5">
                          {input.title}
                        </Typography>
                        {input.customInput}
                      </HeightContainer>
                    </Paper>
                  </Grid>
                );
              }
              break;
            case "upload":
              return (
                <Grid
                  key={input.id}
                  item
                  xs={input.xs}
                  sm={input.sm}
                  className={classes.grid}
                >
                  <Paper className={classes.paper}>
                    <HeightContainer>
                      <Typography color="textPrimary" variant="h5">
                        {input.title}
                      </Typography>
                      file upload
                    </HeightContainer>
                  </Paper>
                </Grid>
              );
            case "mapPoint":
              if (!!input.floorOverlays && !!input.onNewLocation) {
                return (
                  <Grid
                    key={input.id}
                    item
                    xs={input.xs}
                    sm={input.sm}
                    className={classes.grid}
                  >
                    <Paper className={classes.paper}>
                      <HeightContainer>
                        <Typography color="textPrimary" variant="h5">
                          {input.title}
                        </Typography>
                        <ClickLocation
                          floorOverlays={input.floorOverlays}
                          onNewLocation={input.onNewLocation}
                          initialPoint={input.initialPoint}
                          mapId={input.id}
                          cancellable={input.cancellable}
                          onCancel={input.onCancel}
                        />
                      </HeightContainer>
                    </Paper>
                  </Grid>
                );
              } else {
                return (
                  <Grid
                    key={input.id}
                    item
                    xs={input.xs}
                    sm={input.sm}
                    className={classes.grid}
                  >
                    <Paper className={classes.paper}>Error</Paper>
                  </Grid>
                );
              }
          }
        })}
      </Grid>
      {/* </FormWrapper> */}
      {children}
    </Container>
  );
}
